import React, { useState } from "react";

/**
 * ToolLogo — renders a real logo for a tool via Google's favicon service,
 * with a graceful fallback to the colored letter avatar if the image fails
 * to load (Google returns a generic globe icon on true 404).
 *
 * Usage:
 *   <ToolLogo tool={tool} size={48} rounded="xl" />
 */
const domainOf = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const sizeToPx = (size) => (typeof size === "number" ? size : 48);

const roundedClass = (r) => {
  const map = { sm: "rounded", md: "rounded-md", lg: "rounded-lg", xl: "rounded-xl", "2xl": "rounded-2xl", full: "rounded-full" };
  return map[r] || map.xl;
};

const fontSizeFor = (px) => {
  if (px >= 64) return "text-3xl";
  if (px >= 44) return "text-lg";
  if (px >= 32) return "text-sm";
  return "text-xs";
};

export const ToolLogo = ({ tool, size = 48, rounded = "xl", className = "" }) => {
  const [failed, setFailed] = useState(false);
  const px = sizeToPx(size);
  const domain = domainOf(tool?.url);
  const logoSrc = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : "";
  const rc = roundedClass(rounded);
  const commonStyle = { width: px, height: px };

  if (!logoSrc || failed) {
    return (
      <div
        className={`${rc} flex items-center justify-center font-bold text-white ${fontSizeFor(px)} ${className}`}
        style={{ ...commonStyle, background: tool?.logo_color || "#0B1220" }}
        aria-label={tool?.name}
      >
        {tool?.logo_letter || tool?.name?.[0]?.toUpperCase() || "?"}
      </div>
    );
  }

  return (
    <div
      className={`${rc} overflow-hidden flex items-center justify-center bg-white border border-[var(--curio-border,#E3E8F0)] ${className}`}
      style={commonStyle}
    >
      <img
        src={logoSrc}
        alt={`${tool?.name || "Tool"} logo`}
        width={px}
        height={px}
        loading="lazy"
        onError={() => setFailed(true)}
        className="object-contain"
        style={{ width: px * 0.72, height: px * 0.72 }}
      />
    </div>
  );
};

export default ToolLogo;
